import React, { useCallback, useEffect, useRef, useState } from 'react';
import VideoPlayer from './VideoPlayer';
import './QuizEngine.css';
import { resolveAssetUrl } from '../utils/assetUrl';

const INTERACTION_GUARD_MS = 400;

const QuizEngine = ({
    questions,
    checkAnswer,
    onComplete,
    passingScore = 80,
    timeLimit = 0,
    title,
    embedded = false,
    initialQuestionIndex = 0,
    initialAnswers = []
}) => {
    const totalQ = questions.length;
    const [currentQ, setCurrentQ] = useState(() => Math.min(initialQuestionIndex, totalQ));
    const [selectedAnswerIndices, setSelectedAnswerIndices] = useState([]);
    const [revealed, setRevealed] = useState(false);
    const [checking, setChecking] = useState(false);
    const [answers, setAnswers] = useState(() => initialAnswers);
    const [done, setDone] = useState(false);
    const [timeLeft, setTimeLeft] = useState(null);
    const timerRef = useRef(null);
    const interactionBlockedUntilRef = useRef(Date.now() + INTERACTION_GUARD_MS);
    const questionChangeAtRef = useRef(Date.now());
    const pendingSelectionRef = useRef(null);
    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;

    const finishQuiz = useCallback((finalAnswers) => {
        if (done) return;

        setDone(true);
        if (timerRef.current) clearInterval(timerRef.current);

        const score = finalAnswers.filter((answer) => answer.isCorrect).length;
        const total = totalQ;
        const percentage = total > 0 ? Math.round((score / total) * 100) : 0;
        const passed = percentage >= passingScore;

        const detailedAnswers = questions.map((question, index) => {
            const answer = finalAnswers.find((entry) => entry.questionIndex === index);
            
            const givenIndices = answer && Array.isArray(answer.answerIndices) && answer.answerIndices.length > 0
                ? answer.answerIndices
                : (answer && Number.isInteger(answer.answerIndex) ? [answer.answerIndex] : []);
            const givenAnswer = givenIndices.length > 0
                ? givenIndices.map(i => question.options[i] || '—').join(', ')
                : '—';

            const correctIndices = answer && Array.isArray(answer.correctIndices) && answer.correctIndices.length > 0
                ? answer.correctIndices
                : (answer && Number.isInteger(answer.correctIndex) ? [answer.correctIndex] : (answer?.isCorrect ? givenIndices : []));
            const correctAnswer = correctIndices.length > 0
                ? correctIndices.map(i => question.options[i] || '—').join(', ')
                : '—';

            return {
                questionText: question.text,
                image: question.image,
                video: question.video,
                givenAnswer,
                correctAnswer,
                explanation: answer?.explanation || null,
                isCorrect: answer?.isCorrect || false
            };
        });

        onCompleteRef.current({
            score,
            total,
            percentage,
            passed,
            answers: detailedAnswers,
            rawAnswers: finalAnswers.reduce((accumulator, entry) => {
                accumulator[entry.questionIndex] = Array.isArray(entry.answerIndices) && entry.answerIndices.length > 0
                    ? entry.answerIndices
                    : (entry.answerIndex !== undefined && entry.answerIndex !== null ? [entry.answerIndex] : []);
                return accumulator;
            }, [])
        });
    }, [done, passingScore, questions, totalQ]);

    useEffect(() => {
        if (timeLimit > 0) {
            setTimeLeft(timeLimit * 60);
        }

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [timeLimit]);

    useEffect(() => {
        if (timeLeft === null || done) return undefined;
        if (timeLeft <= 0) {
            finishQuiz(answers);
            return undefined;
        }

        timerRef.current = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
        return () => clearInterval(timerRef.current);
    }, [answers, done, finishQuiz, timeLeft]);

    useEffect(() => {
        if (!done && totalQ > 0 && currentQ >= totalQ) {
            finishQuiz(answers);
        }
    }, [answers, currentQ, done, finishQuiz, totalQ]);

    useEffect(() => {
        questionChangeAtRef.current = Date.now();
        interactionBlockedUntilRef.current = Date.now() + INTERACTION_GUARD_MS;
        pendingSelectionRef.current = null;
        setSelectedAnswerIndices([]);
    }, [currentQ]);

    const formatTime = (seconds) => `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;

    const q = questions[currentQ];

    const handleSelect = (answerIndex, event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        if (event?.currentTarget instanceof HTMLElement) {
            event.currentTarget.blur();
        }

        if (revealed || checking) return;
        if (Date.now() < interactionBlockedUntilRef.current) return;
        if (Date.now() - questionChangeAtRef.current < INTERACTION_GUARD_MS) return;

        setSelectedAnswerIndices((prev) => {
            if (prev.includes(answerIndex)) {
                return prev.filter((i) => i !== answerIndex);
            } else {
                return [...prev, answerIndex].sort((a, b) => a - b);
            }
        });
    };

    const handleConfirm = async () => {
        if (selectedAnswerIndices.length === 0 || revealed || checking) return;
        if (pendingSelectionRef.current === currentQ) return;

        pendingSelectionRef.current = currentQ;
        setChecking(true);
        try {
            const response = await checkAnswer(currentQ, selectedAnswerIndices[0], selectedAnswerIndices);
            const entry = {
                questionIndex: currentQ,
                answerIndex: selectedAnswerIndices[0],
                answerIndices: selectedAnswerIndices,
                ...response
            };
            setAnswers((prev) => {
                const existingIndex = prev.findIndex((answer) => answer.questionIndex === currentQ);
                if (existingIndex === -1) return [...prev, entry];

                const next = [...prev];
                next[existingIndex] = { ...next[existingIndex], ...entry };
                return next;
            });
            setRevealed(true);
        } catch (error) {
            console.error('Check answer error:', error);
            pendingSelectionRef.current = null;
        } finally {
            setChecking(false);
        }
    };

    const handleNext = () => {
        if (currentQ < totalQ - 1) {
            setRevealed(false);
            setCurrentQ((prev) => prev + 1);
            return;
        }

        finishQuiz([...answers]);
    };

    const currentAnswer = revealed ? answers.find((answer) => answer.questionIndex === currentQ) : null;
    const selectedIndices = revealed
        ? (currentAnswer && Array.isArray(currentAnswer.answerIndices) && currentAnswer.answerIndices.length > 0
            ? currentAnswer.answerIndices
            : (currentAnswer && Number.isInteger(currentAnswer.answerIndex) ? [currentAnswer.answerIndex] : []))
        : selectedAnswerIndices;

    const optionCls = (optionIndex) => {
        const isSelected = selectedIndices.includes(optionIndex);
        return isSelected ? 'qe-option selected' : 'qe-option';
    };



    if (done) return null;
    if (!q) return null;

    return (
        <div className={`qe-root ${embedded ? 'embedded' : ''}`}>
            {timeLeft !== null && !done && (
                <div className={`qe-timer ${timeLeft < 60 ? 'warning' : ''}`}>
                    ⏲ {formatTime(timeLeft)}
                </div>
            )}

            {!embedded && title && (
                <div className="qe-header">
                    <h2>{title}</h2>
                </div>
            )}

            <div className="qe-progress-wrap">
                <span className="qe-progress-label">Питання {Math.min(currentQ + 1, totalQ)} з {totalQ}</span>
                <div className="qe-progress-bar">
                    <div
                        className="qe-progress-fill"
                        style={{ width: `${totalQ > 0 ? ((currentQ + (revealed ? 1 : 0)) / totalQ) * 100 : 0}%` }}
                    />
                </div>
            </div>

            <div className="qe-card">
                <div className="qe-q-text"><strong>{currentQ + 1}.</strong> {q.text}</div>

                {q.image && (
                    <div className="qe-media">
                        <img src={resolveAssetUrl(q.image)} alt="" className="qe-q-image" />
                    </div>
                )}
                {q.video && (
                    <div className="qe-media video">
                        <VideoPlayer url={q.video} />
                    </div>
                )}

                <div className="qe-options">
                    {q.options.map((option, optionIndex) => (
                        <button
                            type="button"
                            key={optionIndex}
                            className={optionCls(optionIndex)}
                            onClick={(event) => handleSelect(optionIndex, event)}
                            disabled={revealed || checking}
                            style={revealed ? { cursor: 'default' } : {}}
                        >
                            <span className={`qe-checkbox-box ${selectedIndices.includes(optionIndex) ? 'checked' : ''}`}>
                                {selectedIndices.includes(optionIndex) && <span className="qe-checkbox-check">✓</span>}
                            </span>
                            <span className="qe-option-text">{option}</span>
                        </button>
                    ))}
                </div>

                {checking && <div className="qe-checking">Перевірка...</div>}



                <div className="qe-nav">
                    {!revealed && selectedAnswerIndices.length > 0 && !checking && (
                        <button type="button" className="qe-btn-confirm" onClick={handleConfirm}>
                            Підтвердити
                        </button>
                    )}

                    {revealed && (
                        <button type="button" className="qe-btn-next" onClick={handleNext}>
                            {currentQ < totalQ - 1 ? 'Далі →' : 'Завершити'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default QuizEngine;
